import type { JobInfo } from "../../../app/jobs/JobTrackerContext";
import { IMPORT_ADAPTERS } from "./importAdapterRegistry";

/**
 * Einzige Quelle dafür, welche Hintergrundjobs zu einem Adapter-Importlauf gehören. Laufliste,
 * Laufseite, Mandanten-Sperre (`TenantImportCommitOverlay`) und App-Reload (`AppReloadEvents`)
 * fragen hier — ein neuer Adapter meldet nur sein Jobtyp-Präfix in der Registry an.
 */
export const TENANT_BACKUP_RESTORE_JOB_TYPE = "TenantBackupRestore";
export const TENANT_BACKUP_RESTORE_PROGRESS_TOTAL = 6;

export type ImportAdapterJobPhase = "upload" | "analyze" | "precheck" | "commit" | "clearUploads" | "restore" | "other";

const PHASE_BY_SUFFIX: Record<string, ImportAdapterJobPhase> = {
  UploadFiles: "upload",
  Analyze: "analyze",
  Precheck: "precheck",
  Commit: "commit",
  ClearUploads: "clearUploads"
};

function adapterOfJobType(jobType: string) {
  return IMPORT_ADAPTERS.find((adapter) => jobType.startsWith(adapter.jobTypePrefix));
}

/** Job eines Adapters (Upload, Analyse, Vorprüfung, Import, Bereinigung) — ohne Rollback. */
export function isImportAdapterJob(job: Pick<JobInfo, "jobType">) {
  return adapterOfJobType(job.jobType) !== undefined;
}

/** Job eines Adapters oder die Wiederherstellung der Vor-Import-Sicherung. */
export function isImportAdapterRunJob(job: Pick<JobInfo, "jobType">) {
  return isImportAdapterJob(job) || job.jobType === TENANT_BACKUP_RESTORE_JOB_TYPE;
}

export function isImportAdapterCommitJob(job: Pick<JobInfo, "jobType">) {
  return importAdapterJobPhase(job) === "commit";
}

export function importAdapterJobPhase(job: Pick<JobInfo, "jobType">): ImportAdapterJobPhase {
  if (job.jobType === TENANT_BACKUP_RESTORE_JOB_TYPE) return "restore";
  const adapter = adapterOfJobType(job.jobType);
  if (!adapter) return "other";
  const suffix = job.jobType.slice(adapter.jobTypePrefix.length);
  return PHASE_BY_SUFFIX[suffix] ?? "other";
}

export function isActiveJob(job: Pick<JobInfo, "status">) {
  return job.status === "Queued" || job.status === "Running";
}

/** Laufender Adapter-Job (inkl. Rollback) zu genau diesem Lauf. */
export function findActiveImportAdapterRunJob(jobs: Record<string, JobInfo>, runId: string) {
  return Object.values(jobs).find((job) =>
    job.relatedEntityId === runId && isActiveJob(job) && isImportAdapterRunJob(job));
}

/**
 * Schlüssel über alle beendeten Adapter-Jobs eines Laufs — ändert er sich, ist der Lauf neu zu laden.
 * Als Zeichenkette, damit er als Effekt-Abhängigkeit stabil bleibt.
 */
export function finishedImportAdapterRunJobsKey(jobs: Record<string, JobInfo>, runId: string) {
  return Object.values(jobs)
    .filter((job) => job.relatedEntityId === runId && !isActiveJob(job) && isImportAdapterRunJob(job))
    .map((job) => `${job.id}:${job.status}`)
    .sort()
    .join("|");
}

/** Phasenmeldungen des Wiederherstellungsjobs (Backend-Nachrichtenschlüssel). */
const ROLLBACK_PHASE = {
  download: "tenantBackup.job.download",
  verify: "tenantBackup.job.verify",
  import: "tenantBackup.job.import",
  databaseDelete: "tenantBackup.job.databaseDelete",
  databaseImport: "tenantBackup.job.databaseImport",
  files: "tenantBackup.job.files",
  promote: "tenantBackup.job.promote",
  commit: "tenantBackup.job.commit",
  cleanup: "tenantBackup.job.cleanup",
} as const;

export function translateAdapterRollbackPhase(message: string | null | undefined, text: (key: string) => string) {
  const raw = (message ?? "").trim();
  if (!raw) return text("Job title TenantBackupRestore");
  if (raw === ROLLBACK_PHASE.download) return text("Adapter import rollback phase download");
  if (raw === ROLLBACK_PHASE.verify) return text("Adapter import rollback phase verify");
  if (raw === ROLLBACK_PHASE.import) return text("Adapter import rollback phase import");
  if (raw === ROLLBACK_PHASE.databaseDelete) return text("Adapter import rollback phase databaseDelete");
  if (raw === ROLLBACK_PHASE.databaseImport) return text("Adapter import rollback phase databaseImport");
  if (raw === ROLLBACK_PHASE.files) return text("Adapter import rollback phase files");
  if (raw === ROLLBACK_PHASE.promote) return text("Adapter import rollback phase promote");
  if (raw === ROLLBACK_PHASE.commit) return text("Adapter import rollback phase commit");
  if (raw === ROLLBACK_PHASE.cleanup) return text("Adapter import rollback phase cleanup");
  return raw.startsWith("tenantBackup.") ? text(raw) : raw;
}

export function formatAdapterRollbackProgressMessage(message: string | null | undefined, text: (key: string) => string) {
  const phase = translateAdapterRollbackPhase(message, text);
  return text("Adapter import rollback progress").replace("{0}", phase);
}

/** Titel des Fortschrittsstreifens je Jobphase. */
export function importAdapterProgressTitle(job: JobInfo, text: (key: string) => string) {
  switch (importAdapterJobPhase(job)) {
    case "upload":
    case "analyze":
      return text("Import adapter progress server processing");
    case "precheck":
      return text("Import adapter progress prechecking");
    case "commit":
      return text("Import adapter progress committing");
    case "clearUploads":
      return text("Import adapter progress clearing uploads");
    case "restore":
      return text("Import run rollback");
    default:
      return job.title?.trim() || text("Import adapter progress working");
  }
}
